import React, { useState, useEffect } from 'react';
import { neonQuery } from '../services/api';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Printer } from 'lucide-react';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const BOX_TYPES = ['CP11', 'CP18', 'CP30', 'CP34', 'CP42'];
// What ships is the completed unit, not the bare box (Matt, 2026-10-12).
const BOX_LABELS = {
  CP11: 'CP481 - CP11 Completed Unit',
  CP18: 'CP482 - CP18 Completed Unit',
  CP30: 'CP483 - CP30 Completed Unit',
  CP34: 'CP484 - CP34 Completed Unit',
  CP42: 'CP485 - CP42 Completed Unit',
};

const fmtQty = (n) => n % 1 === 0 ? n : n.toFixed(2);

export default function ProductReport() {
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  const [packing, setPacking] = useState([]);
  const [items, setItems] = useState([]);
  const [hardCrabs, setHardCrabs] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('purchaser');

  const load = async (s, e) => {
    setLoading(true);
    setError(null);
    try {
      const [packingRows, itemRows, totalRows, hcRows] = await Promise.all([
        neonQuery('product_report_packing', { startDate: s, endDate: e }),
        neonQuery('product_report_items', { startDate: s, endDate: e }),
        neonQuery('product_report_totals', { startDate: s, endDate: e }),
        neonQuery('product_report_hard_crabs', { startDate: s, endDate: e }),
      ]);
      setPacking(packingRows);
      setItems(itemRows);
      setTotals(totalRows?.[0] || null);
      setHardCrabs(hcRows);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(startDate, endDate); }, []);

  const shiftDate = (days) => {
    const d = new Date(startDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    const iso = d.toISOString().split('T')[0];
    setStartDate(iso);
    setEndDate(iso);
    load(iso, iso);
  };

  const handleDateChange = (s, e) => {
    setStartDate(s);
    setEndDate(e);
    load(s, e);
  };

  // Packing material summary
  const packingMap = {};
  packing.forEach(r => { if (r.box_type) packingMap[r.box_type] = (packingMap[r.box_type] || 0) + Number(r.order_count); });
  const totalDryIce = Number(totals?.total_dry_ice || 0);
  const totalGelPacks = Number(totals?.total_gel_packs || 0);
  // orders.dry_ice holds SCOOPS, not pounds. The live admin's
  // usp_Select_ProductList_BuyerView_With_DateFilter prints
  //   ROUND(sum(dryice) * 0.375, 0) AS Quantity   <- the POUNDS column
  //   'Lbs - Dry Ice (' + sum(dryice) + ' scoops)' <- the raw column, as scoops
  // We had the two the wrong way round (and divided by 2.65 instead of
  // multiplying by 0.375), so 8/24's 296 scoops read as "296 lbs (112 scoops)"
  // where the old report correctly says "111 lbs (296 scoops)".
  const dryIceScoops = Math.round(totalDryIce);
  const dryIceLbs = Math.round(totalDryIce * 0.375);
  // The SP does ROUND(sum(gelpacks) / 36, 0) on integers, so T-SQL integer
  // division truncates. Math.round would round 45.75 up to 46 and disagree.
  const gelPackCases = Math.floor(totalGelPacks / 36);

  // Group items by category, excluding Hard Crabs (handled separately)
  const grouped = {};
  items.forEach(row => {
    const cat = row.category || 'Uncategorized';
    if (cat === 'Hard Crabs') return;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(row);
  });

  // Hard crab detail: split into Male/Female
  const maleRows = hardCrabs.filter(r => r.gender === 'Male');
  const femaleRows = hardCrabs.filter(r => r.gender === 'Female');
  const maleTotal = maleRows.reduce((s, r) => s + Number(r.bushels || 0), 0);
  const femaleTotal = femaleRows.reduce((s, r) => s + Number(r.bushels || 0), 0);

  const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${m}/${d}/${y}`;
  };

  const renderHardCrabRows = (rows, gender) => {
    const total = rows.reduce((s, r) => s + Number(r.bushels || 0), 0);
    return (
      <>
        {rows.map((r, i) => {
          const qty = Number(r.bushels || 0);
          const label = `Bushel ${r.steaming} ${r.size} ${gender} Hard Crabs`.replace(/\s+/g, ' ').trim();
          return (
            <tr key={`hc-${gender}-${i}`}>
              <td></td>
              <td>{fmtQty(qty)}</td>
              <td>{label}</td>
            </tr>
          );
        })}
        <tr style={{ fontWeight: 700, background: 'var(--gray-100)' }}>
          <td></td>
          <td>{fmtQty(total)}</td>
          <td>Total {gender}</td>
        </tr>
      </>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Product Report</h2>
        <button className="btn btn-ghost" onClick={() => window.print()} title="Print">
          <Printer size={16} /> Print
        </button>
      </div>

      {/* Date navigation */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '10px 16px' }}>
          <button className="btn btn-ghost" onClick={() => shiftDate(-7)} title="Back 7 days"><ChevronsLeft size={16} /></button>
          <button className="btn btn-ghost" onClick={() => shiftDate(-1)} title="Back 1 day"><ChevronLeft size={16} /></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" className="form-input" value={startDate}
              onChange={e => handleDateChange(e.target.value, endDate)}
              style={{ width: 150, textAlign: 'center' }} />
            <span style={{ color: 'var(--gray-500)' }}>to</span>
            <input type="date" className="form-input" value={endDate}
              onChange={e => handleDateChange(startDate, e.target.value)}
              style={{ width: 150, textAlign: 'center' }} />
          </div>
          <button className="btn btn-ghost" onClick={() => shiftDate(1)} title="Forward 1 day"><ChevronRight size={16} /></button>
          <button className="btn btn-ghost" onClick={() => shiftDate(7)} title="Forward 7 days"><ChevronsRight size={16} /></button>
        </div>
      </div>

      {loading ? (
        <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>Loading...</div></div>
      ) : error ? (
        <div className="card"><div className="card-body" style={{ color: 'var(--red-600)', padding: 20 }}>{error}</div></div>
      ) : (
        <>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 16 }}>
            <button className={`btn ${view === 'purchaser' ? 'btn-navy' : 'btn-ghost'}`}
              onClick={() => setView('purchaser')} style={{ borderRadius: '6px 0 0 6px' }}>Purchaser View</button>
            <button className={`btn ${view === 'packer' ? 'btn-navy' : 'btn-ghost'}`}
              onClick={() => setView('packer')} style={{ borderRadius: '0 6px 6px 0' }}>Packer View</button>
          </div>

          {/* Header */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ textAlign: 'center', padding: '20px 16px 12px' }}>
              <img src="/logo192.png" alt="Crab Place" style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 8 }} />
              <div style={{ fontWeight: 700, fontSize: 14 }}>The Crab Place</div>
              <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>504 Maryland Avenue</div>
              <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>Crisfield, MD 21817</div>
              <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>Phone: 1-877-328-2722</div>
            </div>
          </div>

          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 16 }}>
            Product List for {formatDate(startDate)} to {formatDate(endDate)}
          </div>

          {/* Packing Material */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">Packing Material</div>
            <div className="card-body" style={{ padding: 0 }}>
              <table className="product-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Quantity</th>
                    <th>Item</th>
                  </tr>
                </thead>
                <tbody>
                  {BOX_TYPES.map(bt => (
                    <tr key={bt}>
                      <td>{packingMap[bt] || 0}</td>
                      <td>{BOX_LABELS[bt]}</td>
                    </tr>
                  ))}
                  <tr>
                    <td>{dryIceLbs}</td>
                    <td>Lbs - Dry Ice ({dryIceScoops} scoops)</td>
                  </tr>
                  <tr>
                    <td>{gelPackCases}</td>
                    <td>Cases - Gel Packs ({Math.round(totalGelPacks)} packs)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Products by Category */}
          <div className="card">
            <div className="card-header">{view === 'purchaser' ? 'Purchaser View' : 'Packer View'}:</div>
            <div className="card-body" style={{ padding: 0 }}>
              <table className="product-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th style={{ width: 100 }}>Quantity</th>
                    <th>Item</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Hard Crabs — detailed with Male/Female subtotals */}
                  {hardCrabs.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={3} style={{ fontWeight: 700, background: 'var(--gray-50)', padding: '8px 12px', color: 'var(--navy)' }}>Hard Crabs</td>
                      </tr>
                      {maleRows.length > 0 && renderHardCrabRows(maleRows, 'Male')}
                      {femaleRows.length > 0 && renderHardCrabRows(femaleRows, 'Female')}
                    </>
                  )}

                  {/* All other categories */}
                  {Object.keys(grouped).length === 0 && hardCrabs.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--gray-400)' }}>No items shipped for this date range</td></tr>
                  ) : (
                    Object.entries(grouped).map(([cat, rows]) => (
                      <React.Fragment key={cat}>
                        <tr>
                          <td colSpan={3} style={{ fontWeight: 700, background: 'var(--gray-50)', padding: '8px 12px', color: 'var(--navy)' }}>{cat}</td>
                        </tr>
                        {rows.map((r, i) => {
                          const qty = Number(r.total_qty || 0);
                          return (
                            <tr key={`${cat}-${i}`}>
                              <td></td>
                              <td>{fmtQty(qty)}</td>
                              <td>{r.item_name?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
